import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPinIcon, ArrowRightIcon, BuildingIcon, SearchIcon, CalendarIcon } from "@/components/Icons";
import { toast } from "sonner";

const EXAMS = ["SSC CGL", "SSC CHSL", "IBPS PO", "IBPS Clerk", "SBI PO", "RBI Grade B"];
const ANY = "__any";
const today = () => new Date().toISOString().slice(0, 10);

export default function Centers() {
    const [sp, setSp] = useSearchParams();
    const [city, setCity] = useState(sp.get("city") || ANY);
    const [exam, setExam] = useState(sp.get("exam") || ANY);
    const [date, setDate] = useState(sp.get("date") || "");
    const [cities, setCities] = useState([]);
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appliedNotice, setAppliedNotice] = useState(""); // e.g. "Showing exam + city — date filter dropped"

    // Refresh city dropdown whenever the picked exam changes
    useEffect(() => {
        const params = exam !== ANY ? { exam } : {};
        api.get("/meta/cities", { params }).then((r) => setCities(r.data.cities || []));
    }, [exam]);

    useEffect(() => {
        if (city !== ANY && cities.length > 0 && !cities.includes(city)) {
            setCity(ANY);
        }
    }, [cities, city]);

    /**
     * Cascade search:
     *  1. exam + city + date  → if empty, toast + drop date
     *  2. exam + city         → if empty, drop city
     *  3. exam                → if still empty, show empty state
     * When exam is "any" we just run whatever filter combination the student picked.
     */
    const runSearch = useCallback(async () => {
        setLoading(true);
        setAppliedNotice("");
        const base = {};
        if (city !== ANY) base.city = city;
        if (exam !== ANY) base.exam = exam;
        try {
            // Attempt 1 — full filter set
            let params = { ...base };
            if (date) params.date = date;
            let r = await api.get("/centers", { params });
            let results = r.data;
            let dropped = [];

            // Cascade only makes sense when exam is set (per spec) and we actually have a date to drop.
            if (results.length === 0 && date && exam !== ANY) {
                toast.info("We don't have availability on this date. Try another date — showing broader results.");
                params = { ...base };
                r = await api.get("/centers", { params });
                results = r.data;
                dropped.push("date");
                if (results.length === 0 && city !== ANY) {
                    params = { exam };
                    r = await api.get("/centers", { params });
                    results = r.data;
                    dropped.push("city");
                }
            }

            if (dropped.length > 0) {
                const kept = ["exam", ...(dropped.includes("city") ? [] : ["city"])].filter(Boolean).join(" + ");
                setAppliedNotice(`Showing ${kept}. Filter${dropped.length > 1 ? "s" : ""} dropped: ${dropped.join(", ")}.`);
            }

            setCenters(results);

            // Reflect *original* selections back to URL for shareability
            const next = new URLSearchParams();
            if (city !== ANY) next.set("city", city);
            if (exam !== ANY) next.set("exam", exam);
            if (date) next.set("date", date);
            setSp(next, { replace: true });
        } finally {
            setLoading(false);
        }
    }, [city, exam, date, setSp]);

    // Auto-run once on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { runSearch(); }, []);

    const clear = () => {
        setCity(ANY); setExam(ANY); setDate("");
        setSp(new URLSearchParams(), { replace: true });
        setAppliedNotice("");
        setTimeout(runSearch, 0);
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10">
            <div className="overline mb-3">Test Center Directory</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Find your exam room.</h1>
            <p className="mt-2 text-muted-foreground text-sm">Real desks, real invigilators, real timers — near you.</p>

            <div className="mt-8 grid md:grid-cols-4 gap-8">
                <aside className="md:col-span-1 space-y-6">
                    <div className="border border-border p-5 bg-card">
                        <div className="overline mb-4">Filters</div>
                        <form
                            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
                            className="space-y-4"
                            data-testid="filters-form"
                        >
                            <div>
                                <label className="text-xs font-medium mb-1 block">Exam</label>
                                <Select value={exam} onValueChange={setExam}>
                                    <SelectTrigger className="rounded-sm" data-testid="filter-exam"><SelectValue placeholder="Any exam" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ANY}>Any exam</SelectItem>
                                        {EXAMS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block">City</label>
                                <Select value={city} onValueChange={setCity} disabled={cities.length === 0}>
                                    <SelectTrigger className="rounded-sm" data-testid="filter-city">
                                        <SelectValue placeholder={cities.length === 0 ? (exam !== ANY ? `No cities for ${exam}` : "No cities yet") : "Any city"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ANY}>Any city</SelectItem>
                                        {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block">Date</label>
                                <Input
                                    type="date"
                                    value={date}
                                    min={today()}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="rounded-sm"
                                    data-testid="filter-date"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button type="submit" className="flex-1 rounded-sm bg-primary hover:bg-primary/90" data-testid="filter-search-btn">
                                    <SearchIcon className="h-4 w-4 mr-2" /> Search
                                </Button>
                                <Button type="button" variant="outline" className="rounded-sm" onClick={clear} data-testid="filter-clear-btn">Clear</Button>
                            </div>
                        </form>
                    </div>
                </aside>

                <div className="md:col-span-3">
                    {appliedNotice && (
                        <div className="mb-3 border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded-sm flex items-center gap-2" data-testid="cascade-notice">
                            <CalendarIcon className="h-3.5 w-3.5" /> {appliedNotice}
                        </div>
                    )}
                    {loading ? (
                        <div className="text-muted-foreground">Loading…</div>
                    ) : centers.length === 0 ? (
                        <div className="border border-dashed border-border p-10 text-center bg-card" data-testid="no-centers">
                            <BuildingIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                            <div className="font-heading font-bold text-lg">No centers found</div>
                            <p className="text-sm text-muted-foreground mt-1">Try clearing filters or picking a different exam / city.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {centers.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/centers/${c.id}`}
                                    className="group grid grid-cols-12 border border-border bg-card hover:border-foreground hover:-translate-y-0.5 transition-all duration-150"
                                    data-testid={`center-row-${c.id}`}
                                >
                                    <div className="col-span-3 aspect-[4/3] bg-muted overflow-hidden">
                                        <img
                                            src={c.photo_urls?.[0] || "https://images.unsplash.com/photo-1574958269340-fa927503f3dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTE4MjY0MXww&ixlib=rb-4.1.0&q=85"}
                                            alt={c.name}
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-300"
                                        />
                                    </div>
                                    <div className="col-span-9 p-5 flex flex-col justify-between">
                                        <div>
                                            <div className="overline text-[10px] flex items-center gap-1"><MapPinIcon className="h-3 w-3" /> {c.city}</div>
                                            <div className="font-heading text-xl font-bold mt-1">{c.name}</div>
                                            <p className="mt-1 text-sm text-muted-foreground">{c.address}</p>
                                        </div>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex gap-2">
                                                {c.amenities?.slice(0, 3).map((a) => (
                                                    <span key={a} className="text-[10px] uppercase tracking-widest border border-border px-2 py-1">{a}</span>
                                                ))}
                                                <span className="text-[10px] uppercase tracking-widest bg-secondary px-2 py-1 font-mono">{c.total_seats} SEATS</span>
                                            </div>
                                            <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform duration-150">
                                                View slots <ArrowRightIcon className="h-4 w-4" />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
